import { normalizeSeatNo } from "./seatUtils";

/**
 * Stable fingerprint of modifiers for cart merge (order-insensitive).
 */
function modifiersFingerprint(modifiers) {
  if (!Array.isArray(modifiers) || modifiers.length === 0) return "";
  return modifiers
    .map((m) => {
      const id = m?.id ?? m?.modifierId ?? m?.MenuItem_ID ?? m?.menuItemId ?? m?.name ?? "";
      const qty = Number(m?.quantity ?? m?.qty ?? 1) || 1;
      const price = Number(m?.price ?? 0) || 0;
      return `${id}:${qty}:${price}`;
    })
    .sort()
    .join(",");
}

function selectedItemFingerprint(si) {
  const itemId = si?.itemId ?? si?.id ?? si?.item?.itemId ?? si?.MenuItem_ID ?? "";
  const variant =
    si?.selectedVariantId ??
    si?.variantId ??
    si?.selectedVariant ??
    si?.variant ??
    "";
  const qty = Number(si?.quantity ?? 1) || 1;
  const auto = si?.isAutoDuplicated ? "1" : "0";
  return `${itemId}|${variant}|${qty}|${auto}|${modifiersFingerprint(si?.modifiers)}`;
}

function dealSlotsFingerprint(dealSlots) {
  if (!Array.isArray(dealSlots)) return "";
  return dealSlots
    .map((slot) => {
      const slotId = slot?.slotId ?? slot?.SlotID ?? "";
      const items = Array.isArray(slot?.selectedItems) ? slot.selectedItems : [];
      const itemsFp = items.map(selectedItemFingerprint).sort().join(";");
      return `${slotId}:{${itemsFp}}`;
    })
    .sort()
    .join("|");
}

function dealProductsFingerprint(dealProducts) {
  if (!Array.isArray(dealProducts)) return "";
  return dealProducts
    .map((p) => {
      const id = p?.productId ?? p?.MenuItem_ID ?? p?.menuItemId ?? p?.id ?? "";
      const variant =
        p?.selectedVariantId ??
        p?.variantId ??
        p?.selectedVariant ??
        p?.variant ??
        p?.variantName ??
        "";
      const qty = Number(p?.quantity ?? 1) || 1;
      return `${id}|${variant}|${qty}|${modifiersFingerprint(p?.modifiers)}`;
    })
    .sort()
    .join(";");
}

/**
 * Merge key for identical deal/combo cart lines.
 * Returns null for normal menu items (those keep one-line-per-add behavior).
 */
export function getDealOrComboMergeKey(item) {
  if (!item) return null;

  const seat = normalizeSeatNo(item.seatNo) ?? "";
  const notes = String(item.specialInstructions || "").trim();
  const price = Number(item.price ?? 0) || 0;

  const isDeal = Boolean(item.isDeal) && Array.isArray(item.dealSlots) && item.dealSlots.length > 0;
  if (isDeal) {
    const dealId =
      item.productId ||
      item.originalItem?.dealId ||
      item.originalItem?.DealID ||
      item.id ||
      "";
    return `deal:${dealId}|seat:${seat}|notes:${notes}|price:${price}|slots:${dealSlotsFingerprint(item.dealSlots)}`;
  }

  const isCombo =
    (item.itemType === "Combo" || item.itemType === "combo") &&
    Array.isArray(item.dealProducts) &&
    item.dealProducts.length > 0;
  if (isCombo) {
    const comboId =
      item.productId ||
      item.id ||
      item.originalItem?.MenuItem_ID ||
      item.originalItem?.id ||
      "";
    return `combo:${comboId}|seat:${seat}|notes:${notes}|price:${price}|products:${dealProductsFingerprint(item.dealProducts)}`;
  }

  return null;
}
